import Vue from 'vue'
import {apiAx} from '../common'
import {assert} from '../../utils'
import {RoutePointTypes} from '../../enums'

export default {
  namespaced: true,
  state: {
    query: {
      qid: null,
      client: null,
      agent: null,
      cargoTypes: [],
      cargo: {}, // uuid to array of measure units with values
      routePoints: {
        origin: null,
        destination: null
      },
      routeByCoordinates: {
        origin: false,
        destination: false
      },
      useAlternatives: {
        origin: false,
        destination: false
      }
    },
    grand: null,
    ratesData: [],
    flights: [],
    selectedRates: [],
    routePointOptions: {
      origin: [],
      destination: []
    },
    linked: null,
    clone: {}, // {request, order?, type, context}
    data: {
      locations: [],
      airlines: [],
      clients: [], // names
      agents: [], // names
      measureUnits: [],
      measureUnitsInfo: [], // TODO: Fetch with API!
      cargoTypes: [],
      currencies: []
    },
    loader: 0
  },
  getters: {
    currentMeasureUnitsInfo: state => state.data.measureUnitsInfo.filter(item => {
      return state.data.measureUnits.some(it => it.code === item.code)
    }),
    groupedMeasureUnits: (state, getters) => {
      const unsorted = getters.currentMeasureUnitsInfo.reduce(
        (acc, mu) => {
          if (!(mu.level in acc)) {
            acc[mu.level] = []
          }
          acc[mu.level].push({name: mu.name, code: mu.code})
          return acc
        }, {}
      )

      const sorted = {}
      Object.keys(unsorted).sort().forEach(key => {
        sorted[key] = unsorted[key]
      })
      return sorted
    },
    cargoTableSchema: (state, getters) => {
      const groupedMeasureUnits = getters.groupedMeasureUnits
      const columns = []
      const groupsFrom = []
      let i = 0
      Object.values(groupedMeasureUnits).forEach(muGroup => {
        if (i !== 0) {
          groupsFrom.push(i)
        }
        muGroup.forEach(mu => {
          i++
          columns.push({label: mu.name, code: mu.code})
        })
      })

      return {
        columns: columns,
        groupsFrom: groupsFrom
      }
    }
  },
  mutations: {
    SET_DATA: (state, params) => {
      Vue.set(state.data, params.entity, params.data)
    },
    SET_QUERY_CLIENT: (state, client) => {
      Vue.set(state.query, 'client', client)
    },
    SET_QUERY_AGENT: (state, agent) => {
      Vue.set(state.query, 'agent', agent)
    },
    TOGGLE_QUERY_CARGO_TYPE: (state, ct) => {
      if (state.query.cargoTypes.includes(ct)) {
        Vue.delete(
          state.query.cargoTypes,
          state.query.cargoTypes.findIndex(item => item === ct)
        )
      } else {
        Vue.set(state.query.cargoTypes, state.query.cargoTypes.length, ct)
      }
    },
    CLEAR_CARGO_TYPES: state => {
      Vue.set(state.data, 'cargoTypes', [])
    },
    SET_ROUTE_POINT_OPTIONS: (state, params) => {
      Vue.set(state.routePointOptions, params.pointType, params.options)
    },
    SET_QUERY_ROUTE_BY_COORDINATES: (state, params) => {
      const pointType = params.pointType
      const isSet = params.isSet
      assert(pointType !== undefined)
      assert(Object.values(RoutePointTypes).includes(pointType))
      assert(isSet === null || typeof isSet === 'boolean')
      Vue.set(state.query.routeByCoordinates, pointType, isSet)
    },
    SET_QUERY_ROUTE_POINT: (state, params) => {
      const pointType = params.pointType
      const location = params.location
      assert(pointType !== undefined)
      assert(Object.values(RoutePointTypes).includes(pointType))
      assert(location === null || typeof location === 'object')

      Vue.set(state.query.routePoints, pointType, location)
    },
    SET_QUERY_USE_ALTERNATIVES: (state, params) => {
      const pointType = params.pointType
      const checked = params.checked
      assert(pointType !== undefined)
      assert(Object.values(RoutePointTypes).includes(pointType))
      assert(typeof checked === 'boolean')

      Vue.set(state.query.useAlternatives, pointType, checked)
    },
    SET_SELECTED_RATE: (state, params) => {
      if (state.selectedRates.includes(params.uuid) && params.value === false) {
        Vue.delete(
          state.selectedRates,
          state.selectedRates.findIndex(item => item === params.uuid)
        )
      } else if (params.value === true) {
        Vue.set(state.selectedRates, state.selectedRates.length, params.uuid)
      }
    },
    CLEAR_SELECTED_RATES: (state) => {
      Vue.set(state, 'selectedRates', [])
    },
    SET_GRAND: (state, grand) => {
      Vue.set(state, 'grand', grand)
    },
    SET_RATES_DATA: (state, data) => {
      state.ratesData = data
    },
    SET_FLIGHTS: (state, flights) => {
      state.flights = flights
    },
    SET_QID: (state, qid) => {
      state.query.qid = qid
    },
    SET_LINKED: (state, linked) => {
      Vue.set(state, 'linked', linked)
    },
    SET_CLONE: (state, clone) => {
      Vue.set(state.clone, 'request', clone ? clone.request : null)
      Vue.set(state.clone, 'order', clone ? clone.order : null)
      Vue.set(state.clone, 'type', clone ? clone.type : null)
      Vue.set(state.clone, 'context', clone ? clone.context : null)
    }
  },
  actions: {
    setQueryUseAlternatives: ({commit}, params) => {
      commit('SET_QUERY_USE_ALTERNATIVES', params)
    },
    setRoutePointOptions: ({commit}, params) => {
      assert(Object.values(RoutePointTypes).includes(params.pointType))
      assert(typeof params.searchString === 'string')

      return apiAx.get(`/locations?searchString=${params.searchString}`)
        .then(response => {
          commit('SET_ROUTE_POINT_OPTIONS', { pointType: params.pointType, options: response.data })
        })
    },
    pickPointFromMap: ({commit}, params) => {
      assert(Object.values(RoutePointTypes).includes(params.pointType))
      assert(typeof params.point === 'object')

      commit('SET_QUERY_ROUTE_BY_COORDINATES', { pointType: params.pointType, isSet: true })
      commit('SET_QUERY_ROUTE_POINT', { pointType: params.pointType, location: params.point })
    },
    setRoutePoint: ({commit}, params) => {
      commit('SET_QUERY_ROUTE_POINT', params)
    },
    setClient: ({commit}, client) => {
      commit('SET_QUERY_CLIENT', client)
    },
    setAgent: ({commit}, agent) => {
      commit('SET_QUERY_AGENT', agent)
    },
    setData: ({commit}, params) => {
      commit('SET_DATA', params)
    },
    toggleCargoType: ({commit}, ct) => {
      commit('TOGGLE_QUERY_CARGO_TYPE', ct)
    },
    clearCargoTypes: ({commit}) => {
      commit('CLEAR_CARGO_TYPES')
    },
    // TODO: data.data is something ugly =(
    setRatesData: ({commit}, data) => {
      commit('SET_RATES_DATA', data.rates)
      commit('SET_GRAND', data.grand)
      commit('SET_QID', data.qid)
    },
    setFlights: ({commit}, flights) => {
      commit('SET_FLIGHTS', flights)
    },
    setQueryRouteByCoordinates: ({commit}, {pointType, isSet}) => {
      commit('SET_QUERY_ROUTE_BY_COORDINATES', { pointType: pointType, isSet: isSet })
    },
    clearQuery: ({commit}) => {
      commit('SET_QID', null)
      commit('SET_RATES_DATA', [])
      commit('SET_QUERY_ROUTE_POINT', { pointType: RoutePointTypes.ORIGIN, location: null })
      commit('SET_QUERY_ROUTE_POINT', { pointType: RoutePointTypes.DESTINATION, location: null })
      commit('SET_QUERY_ROUTE_BY_COORDINATES', { pointType: RoutePointTypes.ORIGIN, isSet: false })
      commit('SET_QUERY_ROUTE_BY_COORDINATES', { pointType: RoutePointTypes.DESTINATION, isSet: false })
    },
    clearRates: ({commit}) => {
      commit('SET_RATES_DATA', [])
    },
    clearFlights: ({commit}) => {
      commit('SET_FLIGHTS', [])
    },
    // TODO: Move to user store
    setSelectedRate: ({commit}, params) => {
      commit('SET_SELECTED_RATE', params)
    },
    // TODO: This one too. And mutation, state and such things...
    clearSelectedRates: ({commit}) => {
      commit('CLEAR_SELECTED_RATES')
    },
    setLinked: ({commit}, linked) => {
      commit('SET_LINKED', linked)
    },
    setClone: ({commit}, clone) => {
      if (clone !== null) {
        assert(
          clone.request && clone.type && clone.context && (clone.order || clone.order === null),
          'Clone object must be null or have order and context fields'
        )
      }
      commit('SET_CLONE', clone)
    }
  }
}



// WEBPACK FOOTER //
// ./src/store/modules/calc.js